import type { Track } from '../api/freemusiers';
import {
  fetchArtistTracks,
  fetchArtists,
  fetchNewest,
  fetchRandomTrack,
  searchSongs,
  trackCoverUrl,
  type ArtistBrowse,
} from '../api/freemusiers';

export type PickerMode = 'newest' | 'search' | 'artist' | 'random';

/** Modes that keep a list open (not one-shot actions). */
type BrowseMode = 'newest' | 'search' | 'artist';

export class TrackPicker {
  readonly el: HTMLElement;
  private listEl: HTMLElement;
  private statusEl: HTMLElement;
  private searchInput: HTMLInputElement;
  /** Last list mode — never leave this stuck on random. */
  private browseMode: BrowseMode = 'newest';
  private onPick: (t: Track) => void;
  private randomBusy = false;
  private gen = 0;

  constructor(onPick: (t: Track) => void) {
    this.onPick = onPick;
    this.el = document.createElement('div');
    this.el.className = 'picker hidden';
    this.el.innerHTML = `
      <div class="picker-panel" role="dialog" aria-label="Choose track">
        <header class="picker-head">
          <h2>SELECT TRACK</h2>
          <button type="button" class="icon-btn" data-close aria-label="Close">✕</button>
        </header>
        <div class="picker-tabs" role="tablist">
          <button type="button" data-mode="newest" class="is-active" role="tab">NEW</button>
          <button type="button" data-mode="search" role="tab">SEARCH</button>
          <button type="button" data-mode="artist" role="tab">ARTIST</button>
          <button type="button" data-mode="random" role="tab" title="Pick a random track">RANDOM</button>
        </div>
        <div class="picker-search-row">
          <input type="search" placeholder="Search songs or artists…" enterkeyhint="search" />
        </div>
        <div class="picker-status">Loading…</div>
        <div class="picker-list" role="listbox"></div>
      </div>
    `;
    this.listEl = this.el.querySelector('.picker-list')!;
    this.statusEl = this.el.querySelector('.picker-status')!;
    this.searchInput = this.el.querySelector('input')!;

    this.el.querySelector('[data-close]')!.addEventListener('click', () => this.close());
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });

    this.el.querySelectorAll('[data-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).dataset.mode as PickerMode;
        void this.setMode(mode);
      });
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void this.runSearch();
    });
  }

  open(): void {
    this.el.classList.remove('hidden');
    // Always reopen on a stable list mode — never auto-fire RANDOM again
    void this.setMode(this.browseMode);
  }

  close(): void {
    this.el.classList.add('hidden');
    this.gen += 1; // cancel in-flight random/list if sheet closed
  }

  private setTabs(active: PickerMode): void {
    this.el.querySelectorAll('[data-mode]').forEach((btn) => {
      const m = (btn as HTMLElement).dataset.mode;
      btn.classList.toggle('is-active', m === active);
      btn.setAttribute('aria-selected', m === active ? 'true' : 'false');
    });
    const showSearch = active === 'search' || active === 'artist';
    this.searchInput.parentElement!.style.display = showSearch ? 'flex' : 'none';
    this.searchInput.placeholder =
      active === 'artist' ? 'Artist name…' : 'Search songs…';
  }

  private async setMode(mode: PickerMode): Promise<void> {
    if (mode === 'random') {
      this.setTabs('random');
      await this.rollRandom();
      return;
    }

    this.browseMode = mode;
    this.setTabs(mode);
    const my = ++this.gen;

    if (mode === 'newest') {
      await this.loadNewest(my);
      return;
    }
    this.statusEl.textContent = mode === 'artist' ? 'Type an artist name' : 'Type to search';
    this.listEl.innerHTML = '';
    if (mode === 'artist') {
      await this.loadArtists(my);
    }
  }

  private async rollRandom(): Promise<void> {
    if (this.randomBusy) return;
    this.randomBusy = true;
    const my = ++this.gen;
    this.statusEl.textContent = 'Rolling the dice…';
    this.listEl.innerHTML = '';
    try {
      const t = await fetchRandomTrack();
      if (my !== this.gen) return; // sheet closed or another action won
      // Snap tabs back to NEW so next MUSIC open doesn't re-roll/close
      this.browseMode = 'newest';
      this.setTabs('newest');
      this.statusEl.textContent = `Random: ${t.title}`;
      this.onPick(t);
      this.close();
    } catch (err) {
      if (my !== this.gen) return;
      this.browseMode = 'newest';
      this.setTabs('newest');
      this.statusEl.textContent = err instanceof Error ? err.message : 'Random failed';
      await this.loadNewest(this.gen);
    } finally {
      this.randomBusy = false;
    }
  }

  private async loadNewest(token: number): Promise<void> {
    this.statusEl.textContent = 'Loading newest…';
    this.listEl.innerHTML = '';
    try {
      const tracks = await fetchNewest(30);
      if (token !== this.gen) return;
      this.renderTracks(tracks);
    } catch (err) {
      if (token !== this.gen) return;
      this.statusEl.textContent = err instanceof Error ? err.message : 'Load failed';
    }
  }

  private async loadArtists(token: number): Promise<void> {
    this.statusEl.textContent = 'Loading artists…';
    try {
      const artists = await fetchArtists(200);
      if (token !== this.gen) return;
      this.listEl.innerHTML = '';
      if (!artists.length) {
        this.statusEl.textContent = 'No artists';
        return;
      }
      this.statusEl.textContent = `${artists.length} artists`;
      for (const a of artists) {
        this.listEl.appendChild(this.artistRow(a));
      }
    } catch (err) {
      if (token !== this.gen) return;
      this.statusEl.textContent = err instanceof Error ? err.message : 'Failed';
    }
  }

  private artistRow(a: ArtistBrowse): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'track-row';
    const initials = artistInitials(a.artist);
    btn.innerHTML = `
      <div class="track-cover avatar-initials" aria-hidden="true">${escapeHtml(initials)}</div>
      <div class="track-meta">
        <div class="track-title">${escapeHtml(a.artist)}</div>
        <div class="track-artist">${a.track_count} tracks</div>
      </div>
    `;
    btn.addEventListener('click', async () => {
      const clickTok = ++this.gen;
      this.statusEl.textContent = `Loading ${a.track_count} tracks by ${a.artist}…`;
      this.listEl.innerHTML = '';
      try {
        const tracks = await fetchArtistTracks({
          artist: a.artist,
          limit: Math.max(200, a.track_count || 100),
        });
        if (clickTok !== this.gen) return;
        this.renderTracks(tracks);
      } catch (err) {
        if (clickTok !== this.gen) return;
        this.statusEl.textContent = err instanceof Error ? err.message : 'Failed';
      }
    });
    return btn;
  }

  private async runSearch(): Promise<void> {
    const q = this.searchInput.value.trim();
    if (q.length < 2) {
      this.statusEl.textContent = 'Need at least 2 characters';
      return;
    }
    const my = ++this.gen;
    this.statusEl.textContent = 'Searching…';
    try {
      const tracks =
        this.browseMode === 'artist'
          ? await fetchArtistTracks({ artist: q, limit: 200 })
          : await searchSongs(q, 50);
      if (my !== this.gen) return;
      this.renderTracks(tracks);
    } catch (err) {
      if (my !== this.gen) return;
      this.statusEl.textContent = err instanceof Error ? err.message : 'Search failed';
    }
  }

  private renderTracks(tracks: Track[]): void {
    this.listEl.innerHTML = '';
    if (!tracks.length) {
      this.statusEl.textContent = 'Nothing found';
      return;
    }
    this.statusEl.textContent = `${tracks.length} tracks`;
    for (const t of tracks) {
      const cover = trackCoverUrl(t.cover_filename);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'track-row';
      btn.innerHTML = `
        ${cover ? `<img class="track-cover" src="${cover}" alt="" loading="lazy" />` : '<div class="track-cover placeholder"></div>'}
        <div class="track-meta">
          <div class="track-title">${escapeHtml(t.title)}</div>
          <div class="track-artist">${escapeHtml(t.artist)}</div>
        </div>
      `;
      btn.addEventListener('click', () => {
        this.onPick(t);
        this.close();
      });
      this.listEl.appendChild(btn);
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function artistInitials(name: string): string {
  const cleaned = name.replace(/[{}]/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
